import { useEffect, useRef, useState } from 'react'
import type { Data, Layout } from 'plotly.js-dist-min'
import styles from './Plot.module.css'

export type PlotProps = {
  data: Data[]
  layout: Partial<Layout>
  /** Describes the chart for screen readers — the canvas itself is opaque. */
  ariaLabel: string
}

type PlotlyApi = {
  react: (
    el: HTMLElement,
    data: Data[],
    layout: Partial<Layout>,
    config?: Record<string, unknown>,
  ) => Promise<unknown>
  purge: (el: HTMLElement) => void
}

const PLOT_CONFIG: Record<string, unknown> = {
  responsive: true,
  displaylogo: false,
  modeBarButtonsToRemove: ['lasso2d', 'select2d'],
}

/** Thin React adapter over plotly.js. Plotly is loaded lazily on first mount so
 * it stays out of the main chat bundle, then driven imperatively via react()
 * and torn down with purge(). */
export default function Plot({ data, layout, ariaLabel }: PlotProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const plotlyRef = useRef<PlotlyApi | null>(null)
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')

  useEffect(() => {
    let cancelled = false
    import('plotly.js-dist-min')
      .then((mod) => {
        if (cancelled) return
        const resolved = (mod as unknown as { default?: PlotlyApi }).default
        plotlyRef.current = resolved ?? (mod as unknown as PlotlyApi)
        setStatus('ready')
      })
      .catch(() => {
        if (!cancelled) setStatus('error')
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const el = containerRef.current
    const plotly = plotlyRef.current
    if (status !== 'ready' || el === null || plotly === null) return
    void plotly.react(el, data, layout, PLOT_CONFIG)
  }, [status, data, layout])

  useEffect(() => {
    const el = containerRef.current
    return () => {
      const plotly = plotlyRef.current
      if (plotly !== null && el !== null) plotly.purge(el)
    }
  }, [])

  return (
    <div className={styles.wrapper}>
      <div ref={containerRef} className={styles.canvas} role="img" aria-label={ariaLabel} />
      {status === 'loading' && (
        <div className={styles.overlay} role="status">
          Laster graf…
        </div>
      )}
      {status === 'error' && (
        <div className={styles.overlay} role="status">
          Kunne ikke laste grafmotoren.
        </div>
      )}
    </div>
  )
}
